import { Controller } from "@hotwired/stimulus";

/**
 * Newsletter Form Controller
 *
 * Handles newsletter subscription form interactions including:
 * - Form submission with Turbo
 * - Loading states
 * - Success/error display
 * - Thank you page redirects
 *
 * Targets:
 * - emailField: Email input field
 * - firstNameField: First name input field (optional)
 * - lastNameField: Last name input field (optional)
 * - submitButton: Form submit button
 * - errorContainer: Error message display container
 *
 * Values:
 * - successMessage: Message to display on successful subscription
 * - thankYouPage: Optional redirect URL after success
 * - formId: Unique form identifier
 * - successContainer: ID of success message container
 */
export default class extends Controller {
  static targets = [
    "emailField",
    "firstNameField",
    "lastNameField",
    "submitButton",
    "errorContainer",
  ];

  static values = {
    successMessage: String,
    thankYouPage: String,
    formId: String,
    successContainer: String,
  };

  /**
   * Initialize controller and set up event listeners
   */
  connect() {
    // Create bound handler functions and store as instance properties
    // This ensures we can remove them properly in disconnect()
    this._handleSubmitStart = this.handleSubmitStart.bind(this);
    this._handleSubmitEnd = this.handleSubmitEnd.bind(this);
    this._handleEmailInput = this.clearErrors.bind(this);
    this._handlePageShow = (event) => {
      if (event.persisted) {
        // Page was loaded from browser cache (back button or refresh)
        this.element.reset();
        this.clearErrors();
      }
    };

    // Check if form has cached values on initial load and clear them
    // This handles cases where browser restores form state on page load
    if (this.hasEmailFieldTarget && this.emailFieldTarget.value) {
      // Small delay to ensure form is fully loaded
      setTimeout(() => {
        // Only clear if we're not currently showing a success message
        const container = document.getElementById(this.successContainerValue);
        if (container && !container.querySelector(".newsletter-success")) {
          this.element.reset();
          this.clearErrors();
        }
      }, 100);
    }

    // Listen for Turbo form submission events
    this.element.addEventListener(
      "turbo:submit-start",
      this._handleSubmitStart,
    );
    this.element.addEventListener("turbo:submit-end", this._handleSubmitEnd);

    // Clear form when navigating back or reloading from cache
    // This prevents form values from persisting in browser cache
    window.addEventListener("pageshow", this._handlePageShow);

    // Clear any previous errors when user starts typing
    if (this.hasEmailFieldTarget) {
      this.emailFieldTarget.addEventListener("input", this._handleEmailInput);
    }
  }

  /**
   * Clean up event listeners
   */
  disconnect() {
    // Remove event listeners using the same bound functions from connect()
    this.element.removeEventListener(
      "turbo:submit-start",
      this._handleSubmitStart,
    );
    this.element.removeEventListener("turbo:submit-end", this._handleSubmitEnd);

    // Remove pageshow event listener
    if (this._handlePageShow) {
      window.removeEventListener("pageshow", this._handlePageShow);
    }

    if (this.hasEmailFieldTarget) {
      this.emailFieldTarget.removeEventListener(
        "input",
        this._handleEmailInput,
      );
    }
  }

  /**
   * Handle form submission start
   * - Disable submit button
   * - Show loading state
   * - Clear previous errors
   */
  handleSubmitStart(event) {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = true;
      this.submitButtonTarget.classList.add("opacity-50", "cursor-not-allowed");

      // Store original button text
      this.originalButtonText = this.submitButtonTarget.textContent;
      this.submitButtonTarget.textContent = "Submitting...";
    }

    this.clearErrors();
  }

  /**
   * Handle form submission end
   * - Re-enable submit button if submission failed
   * - Restore button text
   *
   * @param {Event} event - Turbo submit end event
   */
  handleSubmitEnd(event) {
    const { success } = event.detail;

    if (!success) {
      // Re-enable button if submission failed
      if (this.hasSubmitButtonTarget) {
        this.submitButtonTarget.disabled = false;
        this.submitButtonTarget.classList.remove(
          "opacity-50",
          "cursor-not-allowed",
        );
        this.submitButtonTarget.textContent =
          this.originalButtonText || "Subscribe";
      }
    }
  }

  /**
   * Handle successful subscription
   * Called by Turbo Stream response or directly
   */
  handleSuccess() {
    // Check if we should redirect to thank you page
    if (this.thankYouPageValue && this.thankYouPageValue.trim() !== "") {
      window.location.href = this.thankYouPageValue;
      return;
    }

    // Otherwise, show inline success message
    this.showSuccessMessage();
  }

  /**
   * Show inline success message
   * Replaces form with success message
   */
  showSuccessMessage() {
    // Reset the form to clear all input values
    this.element.reset();

    const container = document.getElementById(this.successContainerValue);
    if (!container) return;

    container.innerHTML = `
      <div class="newsletter-success p-6 bg-green-100 text-green-800 rounded-lg text-center">
        <svg class="w-12 h-12 mx-auto mb-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
        </svg>
        <p class="text-lg font-semibold">${this.escapeHtml(this.successMessageValue)}</p>
      </div>
    `;
  }

  /**
   * Show error message
   *
   * @param {string} message - Error message to display
   */
  showError(message) {
    if (!this.hasErrorContainerTarget) return;

    this.errorContainerTarget.textContent = message;
    this.errorContainerTarget.classList.remove("hidden");

    // Re-enable submit button
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = false;
      this.submitButtonTarget.classList.remove(
        "opacity-50",
        "cursor-not-allowed",
      );
      this.submitButtonTarget.textContent =
        this.originalButtonText || "Subscribe";
    }

    // Scroll error into view if needed
    this.errorContainerTarget.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
    });
  }

  /**
   * Clear error messages
   */
  clearErrors() {
    if (this.hasErrorContainerTarget) {
      this.errorContainerTarget.textContent = "";
      this.errorContainerTarget.classList.add("hidden");
    }
  }

  /**
   * Escape HTML to prevent XSS
   *
   * @param {string} text - Text to escape
   * @returns {string} Escaped text
   */
  escapeHtml(text) {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  }
}
